import { roundToDecimals } from '~/domain/dicom/utils';
import { ProfileUnitGroup } from '~/domain/intensity/lineProfileDatasets';
import { csvEscapeCell } from '~/utils/io/files/handleFileExport';
import { mmToCm } from '~/utils/units';

/**
 * Decimals for an exported value. Deliberately finer than the on-screen
 * `INTENSITY_DISPLAY_DECIMALS`: a relative dose runs 0 to about 1.2, so one
 * decimal would quantise a whole exported profile onto a dozen distinct
 * values. An export is re-analysed, a readout is only read.
 */
const CSV_VALUE_DECIMALS = 4;

/** Header row for a line-profile CSV export. */
export const LINE_PROFILE_CSV_HEADER =
    'dataset,modality,unit,distance_cm,value\n';

/** A rounded numeric CSV cell (shared rounding), or empty for non-finite. */
function numberCell(value: number, decimals: number): string {
    if (!Number.isFinite(value)) {
        return '';
    }
    return `${roundToDecimals(value, decimals)}`;
}

/**
 * CSV rows for a profile export (distance cm, value in display units), every
 * cell formula-injection-safe. The series description is PHI: emitted only
 * when sensitive info is shown, else replaced by a `"<modality> <n>"`
 * placeholder matching the on-screen redaction.
 */
export function buildLineProfileCsvRows(
    unitGroups: readonly ProfileUnitGroup[],
    showSensitiveInfo: boolean
): string[] {
    const rows: string[] = [];
    let datasetIndex = 0;
    for (const group of unitGroups) {
        for (const dataset of group.datasets) {
            datasetIndex += 1;
            const description = dataset.label.trim();
            const label =
                showSensitiveInfo && description
                    ? description
                    : `${dataset.modality} ${datasetIndex}`;
            for (const sample of dataset.samples) {
                rows.push(
                    [
                        csvEscapeCell(label),
                        csvEscapeCell(dataset.modality),
                        csvEscapeCell(dataset.unit),
                        csvEscapeCell(numberCell(mmToCm(sample.distanceMm), 3)),
                        csvEscapeCell(
                            numberCell(
                                dataset.toDisplay(sample.value),
                                CSV_VALUE_DECIMALS
                            )
                        ),
                    ].join(',') + '\n'
                );
            }
        }
    }
    return rows;
}
